import React, { useState, useRef } from 'react';
import { useTheme } from '../components/ThemeContext';
import { API_BASE } from '../../config';
import {
  PortfolioUploadResponse,
  PortfolioRiskSummary,
  PortfolioProduct,
  RiskDistributionItem,
  CountryConcentration,
  MIAGapAnalysisResponse,
  MIASwapItem,
} from '../types/riskAnalyzer';

interface RiskAnalyzerPageProps {
  onSelectProduct: (id: number) => void;
  SectionHeader: React.ComponentType<{ children: React.ReactNode; sub?: string }>;
  ScoreRing: React.ComponentType<{ score: number; size?: number; strokeWidth?: number; color?: string; label?: string }>;
  StatCard: React.ComponentType<{ title: string; value: string | number; subtitle?: string; color?: string; icon?: React.ReactNode; accent?: string }>;
  IconUpload: React.ComponentType;
  IconAlert: React.ComponentType;
  IconShield: React.ComponentType;
  IconDatabase: React.ComponentType;
  IconSpinner: React.ComponentType;
}

// ============================================================
// Supply Chain Risk Analyzer — Upload UI + Portfolio Risk Dashboard
// Issue #70 / B2.4
// ============================================================

type AnalyzerView = 'upload' | 'dashboard';

export default function RiskAnalyzerPage({
  onSelectProduct,
  SectionHeader,
  ScoreRing,
  StatCard,
  IconUpload,
  IconAlert,
  IconShield,
  IconDatabase,
  IconSpinner,
}: RiskAnalyzerPageProps) {
  const { T, getMIAColor, getRiskColor } = useTheme();

  // View state
  const [view, setView] = useState<AnalyzerView>('upload');

  // Upload state
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<PortfolioUploadResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Portfolio dashboard state
  const [portfolioSummary, setPortfolioSummary] = useState<PortfolioRiskSummary | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState<string | null>(null);

  // MIA Gap Analysis state (B2.6)
  const [miaGapData, setMiaGapData] = useState<MIAGapAnalysisResponse | null>(null);
  const [miaGapLoading, setMiaGapLoading] = useState(false);
  const [miaGapError, setMiaGapError] = useState<string | null>(null);

  // Result filter tile state (6 tiles: All, Made in USA, Made in India, Made in China, CCP Impacted, Shortages)
  type ResultFilter = 'all' | 'usa' | 'india' | 'china' | 'ccp' | 'shortages';
  const [resultFilter, setResultFilter] = useState<ResultFilter>('all');

  // Export state (B2.7)
  const [exporting, setExporting] = useState<'pdf' | 'csv' | null>(null);

  // Responsive state
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth <= 1024;

  // ── File validation ────────────────────────────────────────
  const ACCEPTED_EXTENSIONS = ['.csv', '.xls', '.xlsx'];
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

  const validateFile = (file: File): string | null => {
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      return `Invalid file type "${ext}". Please upload a CSV or Excel file (.csv, .xls, .xlsx).`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum size is 10 MB.`;
    }
    return null;
  };

  // ── Drag & drop handlers ───────────────────────────────────
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    setUploadError(null);

    const files = e.dataTransfer.files;
    if (files.length === 0) return;

    const file = files[0];
    const error = validateFile(file);
    if (error) {
      setUploadError(error);
      return;
    }
    setSelectedFile(file);
    setUploadResult(null);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadError(null);
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const error = validateFile(file);
    if (error) {
      setUploadError(error);
      return;
    }
    setSelectedFile(file);
    setUploadResult(null);
  };

  // ── Upload handler ─────────────────────────────────────────
  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      // Simulate progress for UX (actual upload is single POST)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 15;
        });
      }, 300);

      const response = await fetch(`${API_BASE}/api/metasupply/risk-analyzer/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      clearInterval(progressInterval);

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `Upload failed (${response.status})`);
      }

      const data: PortfolioUploadResponse = await response.json();
      setUploadProgress(100);
      setUploadResult(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to upload file. Please try again.';
      setUploadError(message);
    } finally {
      setUploading(false);
    }
  };

  // ── Analyze portfolio handler ──────────────────────────────
  const handleAnalyzePortfolio = async () => {
    if (!uploadResult) return;

    if (!API_BASE) {
      setDashboardError('API base is not configured. Please set REACT_APP_API_BASE.');
      return;
    }

    setDashboardLoading(true);
    setDashboardError(null);

    try {
      const response = await fetch(
        `${API_BASE}/api/metasupply/risk-analyzer/portfolio/${uploadResult.portfolio_id}/summary`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `Analysis failed (${response.status})`);
      }

      const data: PortfolioRiskSummary = await response.json();
      setPortfolioSummary(data);
      setView('dashboard');

      // Fetch MIA gap analysis in parallel
      fetchMIAGapAnalysis(uploadResult.portfolio_id);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to analyze portfolio. Please try again.';
      setDashboardError(message);
    } finally {
      setDashboardLoading(false);
    }
  };

  // ── MIA gap analysis fetch ────────────────────────────────
  const fetchMIAGapAnalysis = async (portfolioId: number) => {
    setMiaGapLoading(true);
    setMiaGapError(null);

    try {
      const response = await fetch(
        `${API_BASE}/api/metasupply/risk-analyzer/portfolio/${portfolioId}/mia-gap`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `MIA gap analysis failed (${response.status})`);
      }

      const data: MIAGapAnalysisResponse = await response.json();
      setMiaGapData(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load MIA gap analysis.';
      setMiaGapError(message);
    } finally {
      setMiaGapLoading(false);
    }
  };

  // ── Export handler (B2.7) ───────────────────────────────
  const handleExport = async (format: 'pdf' | 'csv') => {
    if (!uploadResult || !API_BASE) return;

    setExporting(format);
    try {
      const response = await fetch(
        `${API_BASE}/api/metasupply/risk-analyzer/export/${uploadResult.portfolio_id}?format=${format}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `Export failed (${response.status})`);
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition') || '';
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : `risk-report.${format}`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : `Failed to export ${format.toUpperCase()}.`;
      setDashboardError(message);
    } finally {
      setExporting(null);
    }
  };

  // ── Reset to upload ────────────────────────────────────────
  const handleReset = () => {
    setView('upload');
    setSelectedFile(null);
    setUploadResult(null);
    setUploadProgress(0);
    setUploadError(null);
    setPortfolioSummary(null);
    setDashboardError(null);
    setMiaGapData(null);
    setMiaGapError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // ── Format helpers ─────────────────────────────────────────
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getRiskDistributionColor = (level: string): string => {
    switch (level) {
      case 'low': return T.success;
      case 'medium': return T.warning;
      case 'high': return T.danger;
      default: return T.textLight;
    }
  };

  // ============================================================
  // RENDER: Upload Screen
  // ============================================================
  const renderUploadScreen = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      {/* Hero Section */}
      <div style={{
        position: 'relative',
        background: T.offWhite, border: `1px solid ${T.border}`,
        borderRadius: T.radius,
        padding: isMobile ? '28px 20px' : '40px 44px',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
          }}>
            <IconShield />
            <span style={{
              fontSize: 11, fontWeight: 700, color: T.red,
              textTransform: 'uppercase', letterSpacing: '0.15em',
              fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
            }}>
              Supply Chain Risk Analyzer
            </span>
          </div>
          <h1 style={{
            fontSize: isMobile ? 24 : 32, fontWeight: 800,
            color: T.textDark, letterSpacing: '-0.02em',
            lineHeight: 1.2, marginBottom: 8,
            fontFamily: "'Playfair Display', Georgia, serif",
          }}>
            Analyze Your Portfolio Risk
          </h1>
          <p style={{
            fontSize: 14, color: T.textMid,
            maxWidth: 600, lineHeight: 1.6,
          }}>
            Upload your formulary file to get a comprehensive risk assessment across
            your pharmaceutical portfolio — including MIA scoring, BAA compliance,
            country concentration, and shortage exposure.
          </p>
        </div>
      </div>

      {/* Upload Zone */}
      <div style={{
        background: T.white,
        border: `1px solid ${T.border}`,
        borderRadius: T.radius,
        padding: isMobile ? 20 : 32,
        boxShadow: T.shadow,
      }}>
        <SectionHeader sub="Upload a CSV or Excel file containing NDC codes to analyze">
          Upload Formulary
        </SectionHeader>

        {/* Drag & drop zone */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            marginTop: 20,
            border: `2px dashed ${dragOver ? T.gold : uploadError ? T.danger : T.border}`,
            borderRadius: T.radius,
            padding: isMobile ? '32px 20px' : '48px 40px',
            textAlign: 'center',
            cursor: 'pointer',
            background: dragOver ? `${T.gold}08` : uploadError ? T.dangerBg : T.offWhite,
            transition: 'all 0.2s',
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xls,.xlsx"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          <div style={{
            width: 56, height: 56, borderRadius: T.radius,
            background: dragOver ? `${T.gold}18` : `${T.navy}0A`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
            color: dragOver ? T.gold : T.navy,
          }}>
            <IconUpload />
          </div>

          {selectedFile ? (
            <div>
              <div style={{
                fontSize: 16, fontWeight: 700, color: T.textDark, marginBottom: 4,
              }}>
                {selectedFile.name}
              </div>
              <div style={{ fontSize: 13, color: T.textMid }}>
                {formatFileSize(selectedFile.size)} — Click to change file
              </div>
            </div>
          ) : (
            <div>
              <div style={{
                fontSize: 16, fontWeight: 700, color: T.textDark, marginBottom: 4,
              }}>
                {dragOver ? 'Drop your file here' : 'Drag & drop your formulary file'}
              </div>
              <div style={{ fontSize: 13, color: T.textMid, marginBottom: 12 }}>
                or <span style={{ color: T.gold, fontWeight: 600 }}>click to browse</span>
              </div>
              <div style={{
                display: 'inline-flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center',
              }}>
                {['.CSV', '.XLS', '.XLSX'].map(ext => (
                  <span key={ext} style={{
                    padding: '4px 12px', borderRadius: 20,
                    background: `${T.navy}08`, color: T.textMid,
                    fontSize: 11, fontWeight: 600, letterSpacing: '0.04em',
                  }}>
                    {ext}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Error message */}
        {uploadError && (
          <div style={{
            marginTop: 16, padding: '12px 16px',
            background: T.dangerBg, border: `1px solid #FECACA`,
            borderRadius: T.radiusSm, color: T.danger,
            fontSize: 13, display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <IconAlert />
            {uploadError}
          </div>
        )}

        {/* Dashboard error */}
        {dashboardError && (
          <div style={{
            marginTop: 16, padding: '12px 16px',
            background: T.dangerBg, border: `1px solid #FECACA`,
            borderRadius: T.radiusSm, color: T.danger,
            fontSize: 13, display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <IconAlert />
            {dashboardError}
          </div>
        )}

        {/* Upload progress */}
        {uploading && (
          <div style={{ marginTop: 20 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              marginBottom: 8,
            }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: T.textDark }}>
                Uploading...
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: T.gold }}>
                {Math.round(uploadProgress)}%
              </span>
            </div>
            <div style={{
              width: '100%', height: 6, borderRadius: 3,
              background: T.border, overflow: 'hidden',
            }}>
              <div style={{
                width: `${uploadProgress}%`, height: '100%',
                borderRadius: 3,
                background: `linear-gradient(90deg, ${T.gold}, ${T.navyLight})`,
                transition: 'width 0.3s ease',
              }} />
            </div>
          </div>
        )}

        {/* Upload result */}
        {uploadResult && !uploading && (
          <div style={{
            marginTop: 20, padding: 20,
            background: T.successBg, border: `1px solid #BBF7D0`,
            borderRadius: T.radiusSm,
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              marginBottom: 12,
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke={T.success} strokeWidth="2" strokeLinecap="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <span style={{
                fontSize: 15, fontWeight: 700, color: T.success,
              }}>
                Upload Successful
              </span>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: 12,
            }}>
              <div style={{
                background: T.white, borderRadius: T.radiusSm,
                padding: '12px 16px', textAlign: 'center',
              }}>
                <div style={{
                  fontSize: 24, fontWeight: 800, color: T.navy,
                }}>
                  {uploadResult.total_ndcs}
                </div>
                <div style={{
                  fontSize: 11, color: T.textLight, textTransform: 'uppercase',
                  letterSpacing: '0.08em', fontWeight: 600,
                }}>
                  Total NDCs
                </div>
              </div>
              <div style={{
                background: T.white, borderRadius: T.radiusSm,
                padding: '12px 16px', textAlign: 'center',
              }}>
                <div style={{
                  fontSize: 24, fontWeight: 800, color: T.success,
                }}>
                  {uploadResult.matched_count}
                </div>
                <div style={{
                  fontSize: 11, color: T.textLight, textTransform: 'uppercase',
                  letterSpacing: '0.08em', fontWeight: 600,
                }}>
                  Matched
                </div>
              </div>
              <div style={{
                background: T.white, borderRadius: T.radiusSm,
                padding: '12px 16px', textAlign: 'center',
              }}>
                <div style={{
                  fontSize: 24, fontWeight: 800,
                  color: uploadResult.unmatched_count > 0 ? T.warning : T.success,
                }}>
                  {uploadResult.unmatched_count}
                </div>
                <div style={{
                  fontSize: 11, color: T.textLight, textTransform: 'uppercase',
                  letterSpacing: '0.08em', fontWeight: 600,
                }}>
                  Unmatched
                </div>
              </div>
            </div>

            {uploadResult.unmatched_count > 0 && uploadResult.unmatched_ndcs.length > 0 && (
              <div style={{
                marginTop: 12, padding: '10px 14px',
                background: T.warningBg, borderRadius: T.radiusSm,
                fontSize: 12, color: T.warning,
              }}>
                <span style={{ fontWeight: 700 }}>Unmatched NDCs: </span>
                {uploadResult.unmatched_ndcs.slice(0, 5).join(', ')}
                {uploadResult.unmatched_ndcs.length > 5 && (
                  <span> and {uploadResult.unmatched_ndcs.length - 5} more</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div style={{
          marginTop: 24,
          display: 'flex', gap: 12,
          flexDirection: isMobile ? 'column' : 'row',
        }}>
          {!uploadResult ? (
            <button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              style={{
                padding: '14px 28px', borderRadius: T.radius,
                border: 'none',
                background: !selectedFile || uploading
                  ? T.border
                  : `linear-gradient(135deg, ${T.navy}, ${T.navyLight})`,
                color: !selectedFile || uploading ? T.textLight : T.white,
                fontSize: 14, fontWeight: 700,
                cursor: !selectedFile || uploading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'all 0.2s',
              }}
            >
              {uploading ? (
                <>
                  <IconSpinner />
                  Uploading...
                </>
              ) : (
                <>
                  <IconUpload />
                  Upload File
                </>
              )}
            </button>
          ) : (
            <>
              <button
                onClick={handleAnalyzePortfolio}
                disabled={dashboardLoading}
                style={{
                  padding: '14px 28px', borderRadius: T.radius,
                  border: 'none',
                  background: dashboardLoading
                    ? T.border
                    : `linear-gradient(135deg, ${T.gold}, ${T.goldLight})`,
                  color: dashboardLoading ? T.textLight : T.white,
                  fontSize: 14, fontWeight: 700,
                  cursor: dashboardLoading ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  transition: 'all 0.2s',
                  flex: isMobile ? undefined : 1,
                }}
              >
                {dashboardLoading ? (
                  <>
                    <IconSpinner />
                    Analyzing Portfolio...
                  </>
                ) : (
                  <>
                    <IconShield />
                    Analyze Portfolio
                  </>
                )}
              </button>
              <button
                onClick={handleReset}
                style={{
                  padding: '14px 28px', borderRadius: T.radius,
                  border: `1px solid ${T.border}`,
                  background: T.white,
                  color: T.textMid, fontSize: 14, fontWeight: 600,
                  cursor: 'pointer', transition: 'all 0.2s',
                }}
              >
                Upload Different File
              </button>
            </>
          )}
        </div>
      </div>

      {/* File Format Instructions */}
      <div style={{
        background: T.white,
        border: `1px solid ${T.border}`,
        borderRadius: T.radius,
        padding: isMobile ? 20 : 32,
        boxShadow: T.shadow,
      }}>
        <SectionHeader sub="Ensure your file follows the expected format for best results">
          File Format Instructions
        </SectionHeader>

        <div style={{
          marginTop: 20,
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr 1fr' : '1fr 1fr 1fr',
          gap: 16,
        }}>
          {[
            {
              step: '1',
              title: 'Required Column',
              description: 'Your file must contain a column with NDC codes (10 or 11 digit format). Column header should be "NDC", "ndc_code", or "NDC Code".',
              accent: T.navy,
            },
            {
              step: '2',
              title: 'Optional Columns',
              description: 'You may include "Drug Name", "Quantity", and "Therapeutic Class" columns for richer analysis results.',
              accent: T.gold,
            },
            {
              step: '3',
              title: 'File Format',
              description: 'CSV files should be comma-separated. Excel files (.xls, .xlsx) should have data in the first sheet. Maximum 10,000 rows.',
              accent: T.navyLight,
            },
          ].map(item => (
            <div key={item.step} style={{
              padding: 20, borderRadius: T.radiusSm,
              border: `1px solid ${T.border}`,
              borderLeft: `4px solid ${item.accent}`,
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8,
                background: `${item.accent}14`, color: item.accent,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 800, marginBottom: 12,
              }}>
                {item.step}
              </div>
              <div style={{
                fontSize: 14, fontWeight: 700, color: T.textDark, marginBottom: 6,
              }}>
                {item.title}
              </div>
              <div style={{
                fontSize: 12, color: T.textMid, lineHeight: 1.5,
              }}>
                {item.description}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ============================================================
  // RENDER: Portfolio Risk Dashboard
  // ============================================================
  const renderDashboard = () => {
    if (!portfolioSummary) return null;

    const riskScoreColor = portfolioSummary.aggregate_risk_score >= 70
      ? T.success
      : portfolioSummary.aggregate_risk_score >= 40
        ? T.warning
        : T.danger;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
        {/* Header with back button */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <SectionHeader sub="Comprehensive risk assessment of your uploaded formulary">
              Portfolio Risk Dashboard
            </SectionHeader>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={() => handleExport('csv')}
              disabled={exporting !== null}
              style={{
                padding: '10px 18px', borderRadius: T.radiusSm,
                border: `1px solid ${T.border}`, background: T.white,
                color: T.textDark, fontSize: 13, fontWeight: 600,
                cursor: exporting ? 'wait' : 'pointer', transition: 'all 0.2s',
                opacity: exporting ? 0.6 : 1,
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {exporting === 'csv' ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{
                    width: 14, height: 14, border: `2px solid ${T.textLight}`,
                    borderTopColor: T.navy, borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                  }} />
                  Exporting...
                </span>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Export CSV
                </>
              )}
            </button>
            <button
              onClick={() => handleExport('pdf')}
              disabled={exporting !== null}
              style={{
                padding: '10px 18px', borderRadius: T.radiusSm,
                border: 'none', background: T.navy,
                color: T.white, fontSize: 13, fontWeight: 600,
                cursor: exporting ? 'wait' : 'pointer', transition: 'all 0.2s',
                opacity: exporting ? 0.6 : 1,
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {exporting === 'pdf' ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{
                    width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: T.white, borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                  }} />
                  Exporting...
                </span>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                  Export PDF
                </>
              )}
            </button>
            <button
              onClick={handleReset}
              style={{
                padding: '10px 18px', borderRadius: T.radiusSm,
                border: `1px solid ${T.border}`, background: T.white,
                color: T.textMid, fontSize: 13, fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.2s',
              }}
            >
              New Upload
            </button>
          </div>
        </div>

        {/* Aggregate Risk Score Hero */}
        <div style={{
          background: T.offWhite, border: `1px solid ${T.border}`,
          borderRadius: T.radius, padding: isMobile ? '28px 20px' : '32px 40px',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center', gap: 32,
        }}>
          {/* Left: Score ring */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <ScoreRing
              score={portfolioSummary.aggregate_risk_score}
              size={120}
              strokeWidth={8}
              color={riskScoreColor}
              label="Portfolio Score"
            />
          </div>

          {/* Right: Key metrics */}
          <div style={{ flex: 1, display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 16 }}>
            {[
              { label: 'Products Matched', value: portfolioSummary.matched_products, color: T.textDark },
              { label: 'BAA Eligible', value: `${portfolioSummary.baa_eligible_pct}%`, color: T.success },
              { label: 'Active Shortages', value: portfolioSummary.active_shortages, color: portfolioSummary.active_shortages > 0 ? T.warning : T.success },
            ].map(m => (
              <div key={m.label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: m.color, lineHeight: 1.2 }}>
                  {m.value}
                </div>
                <div style={{
                  fontSize: 10, color: T.textMid,
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                  fontWeight: 600, marginTop: 4,
                  fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
                }}>
                  {m.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stats Cards Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: 16,
        }}>
          <StatCard
            title="Total Products"
            value={portfolioSummary.total_products.toLocaleString()}
            subtitle={`${portfolioSummary.unmatched_count} unmatched`}
            color={T.navy}
            accent="#000000"
            icon={<IconDatabase />}
          />
          <StatCard
            title="BAA Eligible"
            value={`${portfolioSummary.baa_eligible_pct}%`}
            subtitle={`${portfolioSummary.baa_eligible_count} of ${portfolioSummary.matched_products}`}
            color={T.success}
            accent="#000000"
            icon={<IconShield />}
          />
          <StatCard
            title="High Risk"
            value={portfolioSummary.high_risk_count.toLocaleString()}
            subtitle="Require attention"
            color={T.danger}
            accent="#000000"
            icon={<IconAlert />}
          />
          <StatCard
            title="COO Verified"
            value={`${portfolioSummary.matched_products > 0 ? Math.round((portfolioSummary.baa_eligible_count / portfolioSummary.matched_products) * 100) : 0}%`}
            subtitle="Country of origin confirmed"
            color={T.gold}
            accent="#000000"
          />
        </div>

        {/* Risk Distribution + Country Concentration */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: 16,
        }}>
          {/* Risk Distribution */}
          <div style={{
            background: T.white, border: `1px solid ${T.border}`,
            borderRadius: T.radius, padding: 24, boxShadow: T.shadow,
          }}>
            <div style={{
              fontSize: 16, fontWeight: 700, color: T.navy, marginBottom: 20,
            }}>
              Risk Distribution
            </div>
            {renderRiskDistributionChart(portfolioSummary.risk_distribution)}
          </div>

          {/* Country Concentration */}
          <div style={{
            background: T.white, border: `1px solid ${T.border}`,
            borderRadius: T.radius, padding: 24, boxShadow: T.shadow,
          }}>
            <div style={{
              fontSize: 16, fontWeight: 700, color: T.navy, marginBottom: 20,
            }}>
              Country Concentration
            </div>
            {renderCountryConcentrationChart(portfolioSummary.country_concentration)}
          </div>
        </div>

        {/* 6 Filter Tiles */}
        {(() => {
          const allProducts = portfolioSummary.top_risk_products;
          const usaCount = allProducts.filter(p => p.country_of_origin && /united states|usa|us/i.test(p.country_of_origin)).length;
          const indiaCount = allProducts.filter(p => p.country_of_origin && /india/i.test(p.country_of_origin)).length;
          const chinaCount = allProducts.filter(p => p.country_of_origin && /china/i.test(p.country_of_origin)).length;
          const ccpCount = 0; // placeholder — CCP flag not on PortfolioProduct type yet
          const shortageCount = allProducts.filter(p => p.shortage_status && p.shortage_status !== 'None').length;

          const filterTiles: { id: ResultFilter; label: string; count: number; color: string }[] = [
            { id: 'all', label: 'All', count: allProducts.length, color: T.navy },
            { id: 'usa', label: 'Made in USA', count: usaCount, color: T.success },
            { id: 'india', label: 'Made in India', count: indiaCount, color: '#EA580C' },
            { id: 'china', label: 'Made in China', count: chinaCount, color: T.danger },
            { id: 'ccp', label: 'CCP Impacted', count: ccpCount, color: '#7C3AED' },
            { id: 'shortages', label: 'Shortages', count: shortageCount, color: T.warning },
          ];

          const filteredProducts = allProducts.filter(p => {
            if (resultFilter === 'all') return true;
            if (resultFilter === 'usa') return p.country_of_origin && /united states|usa|us/i.test(p.country_of_origin);
            if (resultFilter === 'india') return p.country_of_origin && /india/i.test(p.country_of_origin);
            if (resultFilter === 'china') return p.country_of_origin && /china/i.test(p.country_of_origin);
            if (resultFilter === 'ccp') return false; // placeholder
            if (resultFilter === 'shortages') return p.shortage_status && p.shortage_status !== 'None';
            return true;
          });

          return (
            <>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)',
                gap: 12,
              }}>
                {filterTiles.map(tile => {
                  const isActive = resultFilter === tile.id;
                  return (
                    <div
                      key={tile.id}
                      onClick={() => setResultFilter(tile.id)}
                      style={{
                        padding: '14px 12px',
                        background: isActive ? tile.color : T.white,
                        border: `1.5px solid ${isActive ? tile.color : T.border}`,
                        borderRadius: T.radiusSm,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        boxShadow: isActive ? `0 2px 8px ${tile.color}30` : T.shadow,
                        textAlign: 'center',
                      }}
                    >
                      <div style={{ fontSize: 20, fontWeight: 800, color: isActive ? T.white : tile.color, lineHeight: 1 }}>
                        {tile.count.toLocaleString()}
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: isActive ? 'rgba(255,255,255,0.9)' : T.textDark, marginTop: 4 }}>
                        {tile.label}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Filtered Products Table */}
              <div style={{
                background: T.white, border: `1px solid ${T.border}`,
                borderRadius: T.radius, padding: 24, boxShadow: T.shadow,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  marginBottom: 20,
                }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: T.navy }}>
                    {resultFilter === 'all' ? 'All Products' : filterTiles.find(t => t.id === resultFilter)?.label || 'Products'}
                    <span style={{ fontSize: 13, fontWeight: 500, color: T.textMid, marginLeft: 8 }}>
                      ({filteredProducts.length.toLocaleString()})
                    </span>
                  </div>
                </div>
                {renderTopRiskProducts(filteredProducts)}
              </div>
            </>
          );
        })()}

        {/* MIA Gap Analysis Section (B2.6) */}
        {renderMIAGapAnalysis()}
      </div>
    );
  };

  // ── Risk distribution bar chart ────────────────────────────
  const renderRiskDistributionChart = (distribution: RiskDistributionItem[]) => {
    const total = distribution.reduce((sum, d) => sum + d.count, 0);
    if (total === 0) {
      return (
        <div style={{ textAlign: 'center', padding: 24, color: T.textLight, fontSize: 13 }}>
          No risk data available
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Stacked bar */}
        <div style={{
          display: 'flex', height: 24, borderRadius: 12, overflow: 'hidden',
          background: T.offWhite,
        }}>
          {distribution.filter(d => d.count > 0).map(d => (
            <div
              key={d.level}
              title={`${d.level}: ${d.count} (${d.percentage}%)`}
              style={{
                width: `${d.percentage}%`,
                background: getRiskDistributionColor(d.level),
                transition: 'width 0.6s ease',
              }}
            />
          ))}
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
          {distribution.map(d => (
            <div key={d.level} style={{
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <div style={{
                width: 10, height: 10, borderRadius: 3,
                background: getRiskDistributionColor(d.level),
              }} />
              <span style={{ fontSize: 12, color: T.textMid, textTransform: 'capitalize' }}>
                {d.level}
              </span>
              <span style={{ fontSize: 12, fontWeight: 700, color: T.textDark }}>
                {d.count}
              </span>
              <span style={{ fontSize: 11, color: T.textLight }}>
                ({d.percentage}%)
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── Country concentration horizontal bars ──────────────────
  const renderCountryConcentrationChart = (countries: CountryConcentration[]) => {
    if (countries.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: 24, color: T.textLight, fontSize: 13 }}>
          No country data available
        </div>
      );
    }

    const maxPct = Math.max(...countries.map(c => c.percentage), 1);
    const countryColors: Record<string, string> = {
      'United States': T.success,
      'India': T.warning,
      'China': T.danger,
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {countries.slice(0, 8).map(country => {
          const barColor = countryColors[country.country] || T.navyLight;
          return (
            <div key={country.country}>
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                marginBottom: 4,
              }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.textDark }}>
                  {country.country}
                </span>
                <span style={{ fontSize: 12, color: T.textMid }}>
                  {country.count} ({country.percentage}%)
                </span>
              </div>
              <div style={{
                height: 8, borderRadius: 4, background: T.offWhite,
                overflow: 'hidden',
              }}>
                <div style={{
                  width: `${(country.percentage / maxPct) * 100}%`,
                  height: '100%', borderRadius: 4,
                  background: barColor,
                  transition: 'width 0.6s ease',
                }} />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Top risk products table ────────────────────────────────
  const renderTopRiskProducts = (products: PortfolioProduct[]) => {
    if (products.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: 24, color: T.textLight, fontSize: 13 }}>
          No product data available
        </div>
      );
    }

    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%', borderCollapse: 'collapse',
          fontSize: 13,
        }}>
          <thead>
            <tr>
              {['Product', 'NDC', 'Manufacturer', 'Risk', 'COO', 'BAA'].map(h => (
                <th key={h} style={{
                  textAlign: 'left', padding: '10px 12px',
                  borderBottom: `2px solid ${T.border}`,
                  fontSize: 11, fontWeight: 700, color: T.textLight,
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                  whiteSpace: 'nowrap',
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {products.map((p) => {
              const rc = getRiskColor(p.supply_chain_risk);
              return (
                <tr
                  key={p.id}
                  style={{
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onClick={() => onSelectProduct(p.id)}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLTableRowElement).style.background = T.offWhite;
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLTableRowElement).style.background = 'transparent';
                  }}
                >
                  <td style={{
                    padding: '12px 12px',
                    borderBottom: `1px solid ${T.borderLight}`,
                    fontWeight: 600, color: T.textDark,
                    maxWidth: 200, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {p.product_name}
                  </td>
                  <td style={{
                    padding: '12px 12px',
                    borderBottom: `1px solid ${T.borderLight}`,
                    color: T.textMid, fontFamily: 'monospace', fontSize: 12,
                  }}>
                    {p.ndc}
                  </td>
                  <td style={{
                    padding: '12px 12px',
                    borderBottom: `1px solid ${T.borderLight}`,
                    color: T.textMid,
                  }}>
                    {p.manufacturer}
                  </td>
                  <td style={{
                    padding: '12px 12px',
                    borderBottom: `1px solid ${T.borderLight}`,
                  }}>
                    <span style={{
                      background: rc.bg, color: rc.text,
                      border: `1px solid ${rc.border}`,
                      borderRadius: 20, padding: '3px 10px',
                      fontSize: 10, fontWeight: 700,
                      textTransform: 'uppercase', letterSpacing: '0.04em',
                    }}>
                      {p.supply_chain_risk}
                    </span>
                  </td>
                  <td style={{
                    padding: '12px 12px',
                    borderBottom: `1px solid ${T.borderLight}`,
                    color: T.textMid, fontSize: 12,
                  }}>
                    {p.country_of_origin || '—'}
                  </td>
                  <td style={{
                    padding: '12px 12px',
                    borderBottom: `1px solid ${T.borderLight}`,
                  }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 3,
                      background: p.baa_eligible ? T.successBg : T.dangerBg,
                      color: p.baa_eligible ? T.success : T.danger,
                      border: `1px solid ${p.baa_eligible ? '#BBF7D0' : '#FECACA'}`,
                      borderRadius: 20, padding: '3px 10px',
                      fontSize: 10, fontWeight: 700,
                    }}>
                      {p.baa_eligible ? 'Yes' : 'No'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ============================================================
  // RENDER: MIA Gap Analysis (B2.6)
  // ============================================================
  const renderMIAGapAnalysis = () => {
    // Loading state
    if (miaGapLoading) {
      return (
        <div style={{
          background: T.white, border: `1px solid ${T.border}`,
          borderRadius: T.radius, padding: 40, boxShadow: T.shadow,
          textAlign: 'center',
        }}>
          <IconSpinner />
          <div style={{ fontSize: 14, color: T.textMid, marginTop: 12 }}>
            Analyzing MIA improvement opportunities...
          </div>
        </div>
      );
    }

    // Error state
    if (miaGapError) {
      return (
        <div style={{
          background: T.white, border: `1px solid ${T.border}`,
          borderRadius: T.radius, padding: 24, boxShadow: T.shadow,
        }}>
          <div style={{
            fontSize: 16, fontWeight: 700, color: T.navy, marginBottom: 16,
          }}>
            MIA Gap Analysis
          </div>
          <div style={{
            padding: '12px 16px', background: T.dangerBg,
            border: `1px solid ${getRiskColor('high').border}`, borderRadius: T.radiusSm,
            color: T.danger, fontSize: 13,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <IconAlert />
            {miaGapError}
          </div>
        </div>
      );
    }

    // No data yet
    if (!miaGapData) return null;

    const { current_portfolio_mia, optimized_portfolio_mia, improvement_points,
            total_products, products_with_alternatives, swap_suggestions } = miaGapData;

    const currentColor = getMIAColor(current_portfolio_mia);
    const optimizedColor = getMIAColor(optimized_portfolio_mia);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* MIA Gap Analysis Header Card */}
        <div style={{
          background: T.offWhite, border: `1px solid ${T.border}`,
          borderRadius: T.radius, padding: isMobile ? '24px 20px' : '32px 40px',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke={T.red} strokeWidth="2" strokeLinecap="round">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
              </svg>
              <span style={{
                fontSize: 11, fontWeight: 700, color: T.red,
                textTransform: 'uppercase', letterSpacing: '0.15em',
                fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
              }}>
                MIA Gap Analysis
              </span>
            </div>
            <h2 style={{
              fontSize: isMobile ? 20 : 26, fontWeight: 800,
              color: T.textDark, letterSpacing: '-0.02em',
              lineHeight: 1.2, marginBottom: 6,
              fontFamily: "'Playfair Display', Georgia, serif",
            }}>
              Made in America Improvement Potential
            </h2>
            <p style={{
              fontSize: 13, color: T.textMid,
              maxWidth: 560, lineHeight: 1.5,
            }}>
              {products_with_alternatives > 0
                ? `${products_with_alternatives} of ${total_products} products have American-made alternatives that could improve your portfolio MIA score.`
                : 'No American-made alternatives found for your current portfolio products.'}
            </p>
          </div>
        </div>

        {/* Current vs Optimized — Side by Side Comparison */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr auto 1fr',
          gap: isMobile ? 16 : 0,
          background: T.white, border: `1px solid ${T.border}`,
          borderRadius: T.radius, boxShadow: T.shadow,
          overflow: 'hidden',
        }}>
          {/* Current MIA */}
          <div style={{
            padding: isMobile ? 24 : 32,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
          }}>
            <span style={{
              fontSize: 11, fontWeight: 700, color: T.textLight,
              textTransform: 'uppercase', letterSpacing: '0.1em',
            }}>
              Current Portfolio MIA
            </span>
            <ScoreRing
              score={Math.round(current_portfolio_mia)}
              size={100}
              strokeWidth={7}
              color={currentColor}
            />
            <span style={{
              fontSize: 13, color: T.textMid, marginTop: 4,
            }}>
              {total_products} products analyzed
            </span>
          </div>

          {/* Arrow / Divider */}
          {!isMobile ? (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', padding: '0 20px',
              borderLeft: `1px solid ${T.border}`,
              borderRight: `1px solid ${T.border}`,
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
                stroke={T.gold} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
              <span style={{
                fontSize: 20, fontWeight: 800,
                color: improvement_points > 0 ? T.success : T.textLight,
                marginTop: 8,
              }}>
                +{improvement_points}
              </span>
              <span style={{
                fontSize: 10, color: T.textLight, textTransform: 'uppercase',
                letterSpacing: '0.08em', fontWeight: 600,
              }}>
                points
              </span>
            </div>
          ) : (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 8, padding: '4px 0',
              borderTop: `1px solid ${T.border}`,
              borderBottom: `1px solid ${T.border}`,
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke={T.gold} strokeWidth="2.5" strokeLinecap="round">
                <path d="M12 5v14M5 12l7 7 7-7"/>
              </svg>
              <span style={{
                fontSize: 16, fontWeight: 800,
                color: improvement_points > 0 ? T.success : T.textLight,
              }}>
                +{improvement_points} points
              </span>
            </div>
          )}

          {/* Optimized MIA */}
          <div style={{
            padding: isMobile ? 24 : 32,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
          }}>
            <span style={{
              fontSize: 11, fontWeight: 700, color: T.textLight,
              textTransform: 'uppercase', letterSpacing: '0.1em',
            }}>
              Optimized Portfolio MIA
            </span>
            <ScoreRing
              score={Math.round(optimized_portfolio_mia)}
              size={100}
              strokeWidth={7}
              color={optimizedColor}
            />
            <span style={{
              fontSize: 13, color: T.textMid, marginTop: 4,
            }}>
              {products_with_alternatives} swap{products_with_alternatives !== 1 ? 's' : ''} available
            </span>
          </div>
        </div>

        {/* Improvement Gauge Bar */}
        <div style={{
          background: T.white, border: `1px solid ${T.border}`,
          borderRadius: T.radius, padding: isMobile ? 20 : 24, boxShadow: T.shadow,
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 12,
          }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.navy }}>
              Improvement Potential
            </span>
            <span style={{
              fontSize: 13, fontWeight: 700,
              color: improvement_points > 0 ? T.success : T.textLight,
            }}>
              {current_portfolio_mia}% → {optimized_portfolio_mia}%
            </span>
          </div>

          {/* Full-width gauge */}
          <div style={{
            position: 'relative', height: 28, borderRadius: 14,
            background: T.offWhite, overflow: 'hidden',
          }}>
            {/* Current level */}
            <div style={{
              position: 'absolute', left: 0, top: 0, height: '100%',
              width: `${current_portfolio_mia}%`,
              background: currentColor,
              borderRadius: 14,
              transition: 'width 0.8s ease',
              opacity: 0.35,
            }} />
            {/* Optimized level */}
            <div style={{
              position: 'absolute', left: 0, top: 0, height: '100%',
              width: `${optimized_portfolio_mia}%`,
              background: `linear-gradient(90deg, ${currentColor}, ${optimizedColor})`,
              borderRadius: 14,
              transition: 'width 0.8s ease',
            }} />
            {/* Gap highlight */}
            {improvement_points > 0 && (
              <div style={{
                position: 'absolute', left: `${current_portfolio_mia}%`, top: 0,
                height: '100%',
                width: `${improvement_points}%`,
                background: `repeating-linear-gradient(45deg, transparent, transparent 3px, ${T.gold}25 3px, ${T.gold}25 6px)`,
                borderRadius: '0 14px 14px 0',
              }} />
            )}
          </div>

          {/* Legend row */}
          <div style={{
            display: 'flex', gap: 20, marginTop: 12, flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 12, height: 12, borderRadius: 3,
                background: currentColor, opacity: 0.5,
              }} />
              <span style={{ fontSize: 12, color: T.textMid }}>
                Current ({current_portfolio_mia}%)
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 12, height: 12, borderRadius: 3,
                background: optimizedColor,
              }} />
              <span style={{ fontSize: 12, color: T.textMid }}>
                Optimized ({optimized_portfolio_mia}%)
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 12, height: 12, borderRadius: 3,
                background: `repeating-linear-gradient(45deg, transparent, transparent 2px, ${T.gold}40 2px, ${T.gold}40 4px)`,
                border: `1px solid ${T.gold}40`,
              }} />
              <span style={{ fontSize: 12, color: T.textMid }}>
                Improvement Gap (+{improvement_points} pts)
              </span>
            </div>
          </div>
        </div>

        {/* Swap Suggestions Table */}
        {swap_suggestions.length > 0 && (
          <div style={{
            background: T.white, border: `1px solid ${T.border}`,
            borderRadius: T.radius, padding: isMobile ? 16 : 24, boxShadow: T.shadow,
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: 20, flexWrap: 'wrap', gap: 8,
            }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: T.navy }}>
                  American-Made Alternatives
                </div>
                <div style={{ fontSize: 12, color: T.textLight, marginTop: 2 }}>
                  Products that can be swapped for higher MIA alternatives
                </div>
              </div>
              <div style={{
                padding: '6px 14px', borderRadius: 20,
                background: T.successBg, color: T.success,
                fontSize: 12, fontWeight: 700,
              }}>
                {swap_suggestions.length} swap{swap_suggestions.length !== 1 ? 's' : ''} found
              </div>
            </div>

            {/* Desktop table */}
            {!isMobile ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%', borderCollapse: 'collapse', fontSize: 13,
                }}>
                  <thead>
                    <tr>
                      {['Current Product', 'Country', 'MIA', '', 'Recommended Alternative', 'Alt Country', 'Alt MIA', 'Gain'].map((h, idx) => (
                        <th key={`mia-gap-th-${idx}`} style={{
                          textAlign: 'left', padding: '10px 10px',
                          borderBottom: `2px solid ${T.border}`,
                          fontSize: 10, fontWeight: 700, color: T.textLight,
                          textTransform: 'uppercase', letterSpacing: '0.06em',
                          whiteSpace: 'nowrap',
                        }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {swap_suggestions.map((swap: MIASwapItem) => (
                      <tr key={`${swap.current_product_id}-${swap.alternative_product_id}`}>
                        {/* Current product */}
                        <td style={{
                          padding: '12px 10px',
                          borderBottom: `1px solid ${T.borderLight}`,
                          maxWidth: 180, overflow: 'hidden',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          <div
                            style={{
                              fontWeight: 600, color: T.textDark, cursor: 'pointer',
                            }}
                            title={swap.current_product_name}
                            onClick={() => onSelectProduct(swap.current_product_id)}
                          >
                            {swap.current_product_name}
                          </div>
                          <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>
                            {swap.current_manufacturer}
                          </div>
                        </td>
                        <td style={{
                          padding: '12px 10px',
                          borderBottom: `1px solid ${T.borderLight}`,
                          color: T.danger, fontSize: 12,
                        }}>
                          {swap.current_country}
                        </td>
                        <td style={{
                          padding: '12px 10px',
                          borderBottom: `1px solid ${T.borderLight}`,
                        }}>
                          <ScoreRing score={swap.current_mia_score} size={32} strokeWidth={3} />
                        </td>
                        {/* Arrow */}
                        <td style={{
                          padding: '12px 6px',
                          borderBottom: `1px solid ${T.borderLight}`,
                          textAlign: 'center',
                        }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                            stroke={T.gold} strokeWidth="2" strokeLinecap="round">
                            <path d="M5 12h14M12 5l7 7-7 7"/>
                          </svg>
                        </td>
                        {/* Alternative product */}
                        <td style={{
                          padding: '12px 10px',
                          borderBottom: `1px solid ${T.borderLight}`,
                          maxWidth: 180, overflow: 'hidden',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          <div
                            style={{
                              fontWeight: 600, color: T.success, cursor: 'pointer',
                            }}
                            title={swap.alternative_product_name}
                            onClick={() => onSelectProduct(swap.alternative_product_id)}
                          >
                            {swap.alternative_product_name}
                          </div>
                          <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>
                            {swap.alternative_manufacturer}
                          </div>
                        </td>
                        <td style={{
                          padding: '12px 10px',
                          borderBottom: `1px solid ${T.borderLight}`,
                          color: T.success, fontSize: 12,
                        }}>
                          {swap.alternative_country}
                        </td>
                        <td style={{
                          padding: '12px 10px',
                          borderBottom: `1px solid ${T.borderLight}`,
                        }}>
                          <ScoreRing score={swap.alternative_mia_score} size={32} strokeWidth={3} />
                        </td>
                        {/* Improvement */}
                        <td style={{
                          padding: '12px 10px',
                          borderBottom: `1px solid ${T.borderLight}`,
                        }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            background: T.successBg, color: T.success,
                            border: `1px solid ${getRiskColor('low').border}`,
                            borderRadius: 20, padding: '3px 10px',
                            fontSize: 11, fontWeight: 700,
                          }}>
                            +{swap.mia_improvement}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Mobile card layout */
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {swap_suggestions.map((swap: MIASwapItem) => (
                  <div
                    key={`${swap.current_product_id}-${swap.alternative_product_id}`}
                    style={{
                      border: `1px solid ${T.border}`,
                      borderRadius: T.radiusSm, overflow: 'hidden',
                    }}
                  >
                    {/* Current product */}
                    <div style={{
                      padding: 14, background: T.dangerBg,
                      borderBottom: `1px solid ${T.border}`,
                    }}>
                      <div style={{
                        fontSize: 10, fontWeight: 700, color: T.danger,
                        textTransform: 'uppercase', letterSpacing: '0.08em',
                        marginBottom: 6,
                      }}>
                        Current (Foreign)
                      </div>
                      <div
                        style={{
                          fontSize: 14, fontWeight: 600, color: T.textDark,
                          cursor: 'pointer', marginBottom: 4,
                        }}
                        onClick={() => onSelectProduct(swap.current_product_id)}
                      >
                        {swap.current_product_name}
                      </div>
                      <div style={{
                        display: 'flex', gap: 12, alignItems: 'center',
                        fontSize: 12, color: T.textMid,
                      }}>
                        <span>{swap.current_manufacturer}</span>
                        <span style={{ color: T.danger }}>{swap.current_country}</span>
                        <span style={{ fontWeight: 700 }}>MIA: {swap.current_mia_score}%</span>
                      </div>
                    </div>

                    {/* Arrow */}
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      padding: '6px 0', background: T.offWhite,
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke={T.gold} strokeWidth="2.5" strokeLinecap="round">
                        <path d="M12 5v14M5 12l7 7 7-7"/>
                      </svg>
                      <span style={{
                        fontSize: 12, fontWeight: 700, color: T.success, marginLeft: 6,
                      }}>
                        +{swap.mia_improvement} pts
                      </span>
                    </div>

                    {/* Alternative product */}
                    <div style={{
                      padding: 14, background: T.successBg,
                    }}>
                      <div style={{
                        fontSize: 10, fontWeight: 700, color: T.success,
                        textTransform: 'uppercase', letterSpacing: '0.08em',
                        marginBottom: 6,
                      }}>
                        Recommended (American)
                      </div>
                      <div
                        style={{
                          fontSize: 14, fontWeight: 600, color: T.textDark,
                          cursor: 'pointer', marginBottom: 4,
                        }}
                        onClick={() => onSelectProduct(swap.alternative_product_id)}
                      >
                        {swap.alternative_product_name}
                      </div>
                      <div style={{
                        display: 'flex', gap: 12, alignItems: 'center',
                        fontSize: 12, color: T.textMid,
                      }}>
                        <span>{swap.alternative_manufacturer}</span>
                        <span style={{ color: T.success }}>{swap.alternative_country}</span>
                        <span style={{ fontWeight: 700 }}>MIA: {swap.alternative_mia_score}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Summary footer */}
            <div style={{
              marginTop: 16, padding: '14px 16px',
              background: `${T.gold}0A`, border: `1px solid ${T.gold}25`,
              borderRadius: T.radiusSm,
              display: 'flex', alignItems: 'center', gap: 10,
              flexWrap: 'wrap',
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke={T.gold} strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span style={{ fontSize: 13, color: T.textMid, lineHeight: 1.5 }}>
                Portfolio MIA could improve from{' '}
                <strong style={{ color: currentColor }}>{current_portfolio_mia}%</strong>
                {' '}to{' '}
                <strong style={{ color: optimizedColor }}>{optimized_portfolio_mia}%</strong>
                {' '}by switching {products_with_alternatives} product{products_with_alternatives !== 1 ? 's' : ''} to American-made alternatives.
              </span>
            </div>
          </div>
        )}

        {/* Empty state when no swaps available */}
        {swap_suggestions.length === 0 && (
          <div style={{
            background: T.white, border: `1px solid ${T.border}`,
            borderRadius: T.radius, padding: 32, boxShadow: T.shadow,
            textAlign: 'center',
          }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none"
              stroke={T.success} strokeWidth="1.5" strokeLinecap="round"
              style={{ margin: '0 auto 12px' }}>
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            <div style={{
              fontSize: 16, fontWeight: 700, color: T.navy, marginBottom: 6,
            }}>
              No American-Made Alternatives Found
            </div>
            <div style={{ fontSize: 13, color: T.textMid, maxWidth: 400, margin: '0 auto' }}>
              All products in your portfolio are either already American-made or no
              higher-scoring alternatives are available in our database.
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============================================================
  // MAIN RENDER
  // ============================================================
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {view === 'upload' ? renderUploadScreen() : renderDashboard()}
    </div>
  );
}
